import { useCallback, useRef, useState } from 'react'

import { FileUploadV1TenantPostFeatureEnum } from '@redteclab/api/clients/composition-service-transactions'

import { useAfterSalesApiSwrUploadImage } from '../../after-sales'
import { useContactFormApiPostMutation } from '../../contact-form'
import { useGlobalConfigContext } from '../../global-config'
import { type ContactFormValues } from '../model/ContactFormValues'
import { directContactFormBuildContactRequest } from '../model/directContactFormBuildContactRequest'
import { directContactFormErrorIsAuthenticationError } from '../model/directContactFormErrorIsAuthenticationError'

export type DirectContactFormSubmitStatus =
  'authenticationError' | 'error' | 'idle' | 'success'

export const useDirectContactFormSubmit = ({
  onAuthenticationError,
  onSuccess,
}: {
  onAuthenticationError?: () => void
  onSuccess?: () => void
} = {}): {
  isMutating: boolean
  onSubmit: (values: ContactFormValues) => Promise<void>
  resetSubmitStatus: () => void
  submitStatus: DirectContactFormSubmitStatus
} => {
  const contactMutation = useContactFormApiPostMutation()
  const imageUploadMutation = useAfterSalesApiSwrUploadImage()
  const { tenantAndEnv } = useGlobalConfigContext()
  const [submitStatus, setSubmitStatus] =
    useState<DirectContactFormSubmitStatus>('idle')
  const submissionGeneration = useRef(0)
  const advanceSubmissionGeneration = useCallback((): number => {
    submissionGeneration.current += 1

    return submissionGeneration.current
  }, [])
  const resetSubmitStatus = useCallback((): void => {
    advanceSubmissionGeneration()
    setSubmitStatus('idle')
  }, [advanceSubmissionGeneration])
  const submitContactRequest = useCallback(
    async (values: ContactFormValues): Promise<void> => {
      const imageResponses = await Promise.all(
        values.images.map(async (file) =>
          imageUploadMutation.trigger({
            feature: FileUploadV1TenantPostFeatureEnum.ContactForm,
            file,
            tenant: tenantAndEnv,
          }),
        ),
      )

      const imageIds = imageResponses.flatMap((response) =>
        // eslint-disable-next-line unicorn/no-unnecessary-array-flat-map -- don't reference, refactor if touched
        response.data.fileId ? [response.data.fileId] : [],
      )

      await contactMutation.trigger(
        directContactFormBuildContactRequest({ imageIds, values }),
      )
    },
    [contactMutation, imageUploadMutation, tenantAndEnv],
  )
  const onSubmit = useCallback(
    async (values: ContactFormValues): Promise<void> => {
      const currentSubmissionGeneration = advanceSubmissionGeneration()
      setSubmitStatus('idle')

      try {
        await submitContactRequest(values)
      } catch (error) {
        if (currentSubmissionGeneration !== submissionGeneration.current) {
          return
        }

        const isAuthenticationError = Boolean(
          onAuthenticationError &&
          directContactFormErrorIsAuthenticationError(error),
        )
        setSubmitStatus(isAuthenticationError ? 'authenticationError' : 'error')
        if (isAuthenticationError) {
          onAuthenticationError?.()
        }

        return
      }

      if (currentSubmissionGeneration !== submissionGeneration.current) {
        return
      }

      setSubmitStatus('success')
      onSuccess?.()
    },
    [
      advanceSubmissionGeneration,
      onAuthenticationError,
      onSuccess,
      submitContactRequest,
    ],
  )

  return {
    isMutating: contactMutation.isMutating || imageUploadMutation.isMutating,
    onSubmit,
    resetSubmitStatus,
    submitStatus,
  }
}
